/**
 * RevealPage - Full reveal with comparison and certificate (token-based)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, Download, Clock, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/shared/Header';
import InsightsPanel from '../components/shared/InsightsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const USE_V2_API = process.env.REACT_APP_USE_V2_API === 'true';
const API_V2 = `${BACKEND_URL}/api/v2`;

// Helper for consistent timezone-aware date formatting
const formatDateTime = (isoString, options = {}) => {
  if (!isoString) return '—';
  try {
    const date = new Date(isoString);
    const defaultOptions = {
      dateStyle: 'long',
      timeStyle: 'short',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...options
    };
    return new Intl.DateTimeFormat('en-US', defaultOptions).format(date);
  } catch {
    return isoString;
  }
};

const RevealPage = () => {
  const navigate = useNavigate();
  const { relationshipId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [revealData, setRevealData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notReady, setNotReady] = useState(false);

  useEffect(() => {
    fetchReveal();
  }, [relationshipId, token]);

  const fetchReveal = async () => {
    try {
      // Use relationshipId from URL params, fall back to sessionStorage
      const relId = relationshipId || sessionStorage.getItem('relationship_id');
      
      // Use V2 API for reveal endpoint (better comparison logic + debug support)
      const baseUrl = USE_V2_API ? API_V2 : API;
      let url = USE_V2_API 
        ? `${baseUrl}/relationships/${relId}/reveal`
        : `${baseUrl}/reveal/${relId}`;
      
      // Add query params
      const params = new URLSearchParams();
      if (token) params.append('token', token);
      if (USE_V2_API) params.append('debug', 'true');
      if (params.toString()) url += `?${params.toString()}`;
      
      console.log(`[API] Using ${USE_V2_API ? 'V2' : 'V1'} reveal endpoint:`, url);
      
      const response = await axios.get(url);
      
      if (response.data?.comparison?.scoring_version) {
        console.log('[API] Comparison scoring_version:', response.data.comparison.scoring_version);
      }
      
      if (!response.data.ready) {
        setNotReady(true);
        setRevealData(response.data);
      } else {
        setRevealData(response.data);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching reveal:', error);
      if (error.response?.status === 403) {
        toast.error('Invalid or expired reveal link');
      } else {
        toast.error('Failed to load reveal');
      }
      setLoading(false);
    }
  };

  const handleDownloadCertificate = async () => {
    try {
      const relId = relationshipId || sessionStorage.getItem('relationship_id');
      const response = await axios.get(`${API}/certificates/${relId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `MarrsingClause-Certificate-${revealData.certificate?.public_certificate_id || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificate downloaded!');
    } catch (error) {
      toast.error('Failed to download certificate');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center" style={{minHeight: 'calc(100vh - 100px)'}}>
          <Loader2 className="w-8 h-8 animate-spin text-stone-900" />
        </div>
      </div>
    );
  }

  if (notReady) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center p-6" style={{minHeight: 'calc(100vh - 100px)'}}>
          <div className="max-w-lg w-full bg-white rounded-2xl border border-stone-100 shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="text-3xl font-bold text-stone-900 mb-4">Reveal Not Ready Yet</h2>
            <p className="text-stone-600 mb-6">{revealData?.message || 'Waiting for both partners to sign.'}</p>
            
            <div className="flex gap-4 justify-center text-sm text-stone-500 mb-8">
              <span className={revealData?.partner_a_signed ? 'text-mc-blue-deep' : ''}>
                Partner A: {revealData?.partner_a_signed ? '✓ Signed' : 'Pending'}
              </span>
              <span className={revealData?.partner_b_signed ? 'text-mc-blue-deep' : ''}>
                Partner B: {revealData?.partner_b_signed ? '✓ Signed' : 'Pending'}
              </span>
            </div>
            
            <button
              onClick={fetchReveal}
              className="bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-8 py-3 font-medium"
            >
              Check Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Handle case where revealData is null (API error)
  if (!revealData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center p-6" style={{minHeight: 'calc(100vh - 100px)'}}>
          <div className="max-w-lg w-full bg-white rounded-2xl border border-stone-100 shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-3xl font-bold text-stone-900 mb-4">Reveal Not Found</h2>
            <p className="text-stone-600 mb-6">This reveal link may be invalid or expired.</p>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-8 py-3 font-medium"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { comparison, certificate, partners, level } = revealData;
  const partnerAName = partners?.[0]?.first_name || 'Partner A';
  const partnerBName = partners?.[1]?.first_name || 'Partner B';

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-mc-blue-pale rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-mc-blue-deep" />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 mb-3 sm:mb-4 break-words px-2" data-testid="reveal-heading">
              Your MarrsingClause® Reveal
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-stone-600 px-2">
              Both signatures complete. Your locked responses have been unlocked.
            </p>
          </div>

          {/* Alignment Summary */}
          {comparison && (
            <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-4 sm:p-6 md:p-8 mb-6 sm:mb-8">
              <h3 className="text-xl sm:text-2xl font-bold text-stone-900 mb-4 sm:mb-6">Your Alignment</h3>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 mb-6 sm:mb-8">
                <div className="text-center sm:text-left">
                  <span className="text-4xl sm:text-5xl md:text-6xl font-bold text-stone-900">
                    {comparison.alignment_score?.toFixed(0) || 0}%
                  </span>
                  <p className="text-sm sm:text-base text-stone-600 mt-2">Alignment Score</p>
                </div>
                <div className="text-sm sm:text-base text-stone-600">
                  <p>{comparison.diff_summary}</p>
                </div>
              </div>

              {/* Comparison Grid */}
              <div className="space-y-4">
                {comparison.sections?.map((section, idx) => (
                  <div key={idx} className="border border-stone-100 rounded-xl p-3 sm:p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">{section.category}</p>
                        <p className="font-medium text-stone-900 text-sm sm:text-base break-words">{section.prompt}</p>
                      </div>
                      <div className="ml-2 sm:ml-4 flex-shrink-0">
                        {section.match ? (
                          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-mc-blue-pale rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 sm:w-4 sm:h-4 text-mc-blue-deep" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-amber-100 rounded-full flex items-center justify-center">
                            <X className="w-3 h-3 sm:w-4 sm:h-4 text-amber-600" />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div className="bg-blue-50 rounded-lg p-3">
                        <span className="text-xs font-medium text-blue-700 block mb-1">{partnerAName}</span>
                        <span className="text-stone-700 text-sm break-words">
                          {typeof section.partner_a_answer === 'string' 
                            ? section.partner_a_answer 
                            : JSON.stringify(section.partner_a_answer)}
                        </span>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-3">
                        <span className="text-xs font-medium text-purple-700 block mb-1">{partnerBName}</span>
                        <span className="text-stone-700 text-sm break-words">
                          {typeof section.partner_b_answer === 'string' 
                            ? section.partner_b_answer 
                            : JSON.stringify(section.partner_b_answer)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Insights Panel */}
          {comparison?.insights && (
            <InsightsPanel 
              insights={comparison.insights} 
              partnerAName={partnerAName} 
              partnerBName={partnerBName}
              relationshipId={relationshipId || sessionStorage.getItem('relationship_id')}
            />
          )}

          {/* Signatures */}
          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-4 sm:p-6 md:p-8 mb-6 sm:mb-8">
            <h3 className="text-xl sm:text-2xl font-bold text-stone-900 mb-4 sm:mb-6">Signatures</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {partners?.map((partner, idx) => (
                <div key={idx} className="border border-stone-100 rounded-xl p-4 sm:p-6">
                  <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">Partner {partner.role}</p>
                  <p className="text-lg sm:text-xl font-semibold text-stone-900 mb-2 break-words">
                    {partner.first_name} {partner.last_name}
                  </p>
                  <p className="text-base sm:text-lg italic text-stone-700 border-b border-stone-200 pb-2 mb-2 break-words">
                    {partner.typed_signature}
                  </p>
                  <p className="text-xs sm:text-sm text-stone-500">
                    Signed: {formatDateTime(partner.signed_at)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Certificate Download */}
          {certificate && (
            <div className="bg-mc-blue-pale rounded-2xl border border-[var(--mc-border-blue)] p-4 sm:p-6 md:p-8 text-center">
              <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-mc-blue-deep mx-auto mb-4" />
              <h3 className="text-xl sm:text-2xl font-bold text-stone-900 mb-2">Certificate Ready</h3>
              <p className="text-sm sm:text-base text-stone-600 mb-4 sm:mb-6 break-all">
                Certificate ID: {certificate.public_certificate_id}
              </p>
              <button
                onClick={handleDownloadCertificate}
                className="bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-2"
                data-testid="download-certificate"
              >
                <Download className="w-5 h-5" />
                Download PDF Certificate
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevealPage;
